/** Copy + CTA registry for every signal_key the backend rule engine can fire.
 *
 * Ground truth for WHICH keys exist and what fields each one's evidence
 * carries is `backend/utils/signal_registry.py::evaluate_all_signals()` —
 * NOT the mock or either of the two frontend maps this file used to split
 * across (this file's old `SIGNAL_CTA_LABEL` + `ActionCard.tsx`'s local
 * `SIGNAL_TITLES`). `title`/CTA label copy is ported verbatim from the
 * prototype's `evaluateSignal()`/`fcPrimaryAction()`
 * (my-accounts-portfolio-v4.html, ~lines 7708-8008); `textTemplate`/
 * `whyTemplate` are also ported from there but rebound to the field names
 * `signal_registry.py`'s `fire(key, tier, title, **fields)` calls actually
 * put on the wire as `evidence.triggeredFields` (confirmed against
 * `signal_evaluator_service.py`'s `evidence={"triggeredFields": ...}`
 * construction) — the mock's local JS vars (`a.risk`, `a.opportunity`,
 * `a.bhDims.growth`, `T.BH_STRONG`, etc.) are NOT all present in real
 * evidence; where a mock template referenced a field the API doesn't send
 * (e.g. rl-portfolio-risk/rl-expansion/rl-blocked only send the categorical
 * `portfolio_risk`/`expansion_opportunity`/`rl_conditions` enum, not the
 * underlying numeric risk/opportunity score), the copy below was reworded to
 * only reference what evidence actually contains.
 *
 * `al-goal-gap` has no mock equivalent (a backend-only rule comparing `al`
 * against its target AL: committed, else public) — its copy is written from the rule's own
 * condition, not invented business narrative.
 */

/** "path" = an admin-authored Target Registry page; the card opens the
 * CTA's own `path` (see fillCtaPath). */
export type CTAKind =
  | "assess"
  | "relationships"
  | "synopsis"
  | "path"
  | "brief";

/** Account Actions vs Relationship Actions sub-tab classification (mock's
 * Actions tab inner-tab-bar, ~2227-2250). This is NOT the same axis as
 * `ctaKind` — several account-level signals (e.g. bh-ah-aircover) route
 * their primary CTA to the relationships page but are still account-tier
 * concerns. The mock's own "Relationship Action Cards" stub names exactly
 * four signal keys as relationship-tier: rl-unmapped, rl-unassessed,
 * rl-low-for-cluster, sponsor-mismatch — everything else is account-tier. */
export type SignalBucket = "account" | "relationship";

export interface SignalCopyEntry {
  /** Ported verbatim from the mock's evaluateSignal() `title` field. */
  title: string;
  /** {{field}} placeholders resolved against evidence.triggeredFields. */
  textTemplate: string;
  /** {{field}} placeholders resolved against evidence.triggeredFields. */
  whyTemplate: string;
  primaryActionLabel: string;
  ctaKind: CTAKind;
  bucket: SignalBucket;
}

/** Fallback for a key with no entry below — neutral, mirrors the
 * backend's signal_catalog.GENERIC_CTA. */
const GENERIC_CTA_LABEL = "View account";

export const SIGNAL_COPY: Record<string, SignalCopyEntry> = {
  "renewal-no-foundation": {
    title: "Renewal + No Foundation",
    textTemplate:
      "Renewal in {{renewal_days}} days with AL{{al}} and RL{{rl}} — alignment or relationship depth is critically low. Immediate intervention needed.",
    whyTemplate:
      "Renewal < {{renewal-critical-days}} days + AL ≤ {{foundation-score-max}} or RL ≤ {{foundation-score-max}}",
    primaryActionLabel: "Review renewal plan",
    ctaKind: "synopsis",
    bucket: "account",
  },
  "triage-stale-renewal": {
    title: "Stale Alignment + Near Renewal",
    textTemplate:
      "Renewal in {{renewal_days}} days, but the alignment picture is outdated — last assessed {{al_stale_days}} days ago. Refresh the assessment before entering renewal discussions.",
    whyTemplate:
      "Account Health in {{triage-clusters}} + AL staleness > {{al-stale-days}}d + renewal < {{renewal-near-days}}d",
    primaryActionLabel: "Refresh AL assessment",
    ctaKind: "assess",
    bucket: "account",
  },
  "triage-bh-concern": {
    // Triage = Account Health < 55, not low alignment (signal audit, 30 Sep).
    title: "Low Account Health + Business Health Concern",
    textTemplate:
      "Business Health score {{bh}} signals external concern, on top of low Account Health. Review account posture before the next call.",
    whyTemplate:
      "Account Health in {{triage-clusters}} + Business Health < {{rule-bh-concern-below}}",
    // Names the action, not "Synopsis →" (CTA label pass, 1 Oct).
    primaryActionLabel: "Review account posture",
    ctaKind: "synopsis",
    bucket: "account",
  },
  "cluster-triage": {
    // Fires on Account Health < 55 — refreshing AL can't clear it.
    title: "Low Account Health — Immediate Attention",
    textTemplate:
      "Account Health is in the {{triage-clusters}} cluster. Without intervention this account is at retention risk.",
    whyTemplate: "Account Health in {{triage-clusters}}",
    primaryActionLabel: "View KPI detail",
    ctaKind: "synopsis",
    bucket: "account",
  },
  "fragile-alignment": {
    title: "Fragile Alignment",
    textTemplate:
      "Strategic alignment is strong (AL{{al}}) but personal executive relationships are thin (RL{{rl}}). A single leadership change could collapse the program. Broaden the relationship base now.",
    whyTemplate: "High AL + Low RL → Fragile Alignment",
    primaryActionLabel: "Review priority relationships",
    ctaKind: "relationships",
    bucket: "account",
  },
  "al-stale-defend-grow": {
    title: "AL Assessment Stale",
    textTemplate:
      "AL{{al}} has not been reassessed in {{al_stale_days}} days. Stale alignment data weakens renewal and prioritisation decisions — schedule a refresh this week.",
    whyTemplate: "AL staleness > {{al-stale-days}}d",
    primaryActionLabel: "Start AL self-assessment",
    ctaKind: "assess",
    bucket: "account",
  },
  "kpi-threshold": {
    title: "KPI Below Threshold",
    textTemplate:
      "Account Health score {{ah}} is below threshold — a sustained low score increases cluster reclassification risk.",
    whyTemplate: "Account Health < {{ah-warning-threshold}}",
    primaryActionLabel: "View KPI detail",
    ctaKind: "synopsis",
    bucket: "account",
  },
  "bh-concern": {
    title: "Business Health Concern",
    textTemplate:
      "Business Health score {{bh}} shows external concern signals. Review may reveal strategic context for your next conversation.",
    whyTemplate: "Business Health < {{rule-bh-concern-below}}",
    // Names the action, not "Synopsis →" (CTA label pass, 1 Oct).
    primaryActionLabel: "Review business health",
    ctaKind: "synopsis",
    bucket: "account",
  },
  "bh-ah-aircover": {
    title: "RL Air Cover Needed",
    textTemplate:
      "External conditions are favorable (BH {{bh}}) but Account Health is lagging (AH {{ah}}). Exec relationship strength is the air cover while the team resolves KPI issues — sustain and reinforce sponsor cadences now.",
    whyTemplate:
      "Business Health > {{rule-bh-strong-above}} + Account Health < {{ah-aircover-threshold}}",
    primaryActionLabel: "Brief exec sponsors",
    ctaKind: "relationships",
    bucket: "account",
  },
  "bh-financial": {
    title: "Financial Performance Signal",
    textTemplate:
      "External financial signals show pressure — potential procurement scrutiny ahead.",
    whyTemplate: "BH financial dimension: {{bh_financial}}",
    // Names the action, not "Synopsis →" (CTA label pass, 1 Oct).
    primaryActionLabel: "Review financial signals",
    ctaKind: "synopsis",
    bucket: "account",
  },
  "bh-leadership": {
    title: "Leadership Instability",
    textTemplate:
      "C-suite or board changes detected — map exec contacts before your planned outreach shifts.",
    whyTemplate: "BH leadership dimension: {{bh_leadership}}",
    primaryActionLabel: "Map exec contacts",
    ctaKind: "relationships",
    bucket: "account",
  },
  "bh-legal": {
    title: "Legal / Compliance Signal",
    textTemplate:
      "Regulatory or litigation signals present — procurement freeze risk; elevate call priority.",
    whyTemplate: "BH legal dimension: {{bh_legal}}",
    // Names the action, not "Synopsis →" (CTA label pass, 1 Oct).
    primaryActionLabel: "Elevate call priority",
    ctaKind: "synopsis",
    bucket: "account",
  },
  "bh-growth": {
    title: "Growth Opportunity Signal",
    textTemplate:
      "Strong hiring and expansion signals detected — a growth conversation window may be open.",
    whyTemplate: "BH growth dimension: {{bh_growth}}",
    // Names the action, not "Synopsis →" (CTA label pass, 1 Oct).
    primaryActionLabel: "Plan growth conversation",
    ctaKind: "synopsis",
    bucket: "account",
  },
  "rl-portfolio-risk": {
    title: "Exec Contact Risk",
    textTemplate:
      "External signals indicate leadership instability — map continuity contacts before planned outreach shifts.",
    whyTemplate: "Portfolio risk composite: {{portfolio_risk}}",
    primaryActionLabel: "Map continuity contacts",
    ctaKind: "relationships",
    bucket: "account",
  },
  "rl-expansion": {
    title: "Relationship Expansion Opportunity",
    textTemplate:
      "Strong growth and opportunity signals — expand the executive relationship surface now into new business units.",
    whyTemplate: "Expansion opportunity: {{expansion_opportunity}}",
    primaryActionLabel: "Stakeholder mapping →",
    ctaKind: "relationships",
    bucket: "account",
  },
  "rl-blocked": {
    title: "RL Conditions: Blocked",
    textTemplate:
      "External risk and business health signals suggest pausing new RL investment — focus on maintaining existing contacts.",
    whyTemplate: "RL conditions: {{rl_conditions}}",
    // Names the action, not "Synopsis →" (CTA label pass, 1 Oct).
    primaryActionLabel: "Review risk signals",
    ctaKind: "synopsis",
    bucket: "account",
  },
  "renewal-warning": {
    title: "Renewal in Window",
    textTemplate:
      "Renewal in {{renewal_days}} days. Confirm alignment is solid before renewal discussions begin.",
    whyTemplate:
      "Renewal {{renewal-warning-start}}–{{renewal-warning-end}} days",
    primaryActionLabel: "Review renewal plan",
    ctaKind: "synopsis",
    bucket: "account",
  },
  "warm-uncommitted": {
    title: "Warm but Uncommitted",
    textTemplate:
      "Strong executive relationships (RL{{rl}}) haven't converted into strategic alignment (AL{{al}}). The relationship capital is in place — introduce the alignment framework now.",
    whyTemplate: "Low AL + Strong RL → Warm but Uncommitted",
    primaryActionLabel: "Secure a commitment",
    ctaKind: "synopsis",
    bucket: "account",
  },
  "ready-to-advance": {
    title: "Ready to Advance",
    textTemplate:
      "Strong executive relationships (RL{{rl}}) and solid alignment (AL{{al}}) — no recent AL progress in {{al_stale_days}} days. Use the relationship warmth to advance the alignment conversation.",
    whyTemplate:
      "Mid AL + Strong RL + no AL progress > {{al-stale-days}}d — stays open until AL or RL changes band",
    primaryActionLabel: "Pre-call Brief →",
    ctaKind: "brief",
    bucket: "account",
  },
  "strategic-alignment": {
    title: "Strategic Alignment",
    textTemplate:
      "AL{{al}} with RL{{rl}} — strong alignment and relationship depth. This account is ready for a growth or expansion conversation.",
    whyTemplate: "High AL + High RL → Strategic Alignment",
    primaryActionLabel: "Plan expansion conversation",
    ctaKind: "synopsis",
    bucket: "account",
  },
  "dependent-alignment": {
    title: "Dependent Alignment",
    textTemplate:
      "Alignment is solid (AL{{al}}) but rests on a narrow relationship base (RL{{rl}}). Add at least one additional executive contact before the next renewal cycle.",
    whyTemplate: "High AL + Mid RL → Aligned but Spread Thin",
    primaryActionLabel: "Review priority relationships",
    ctaKind: "relationships",
    bucket: "account",
  },
  "al-due": {
    title: "Assessment Due",
    textTemplate:
      "AL self-assessment overdue — last assessed {{al_stale_days}} days ago. A fresh assessment confirms progress toward the AL goal.",
    whyTemplate: "AL assessment age {{al-due-start}}–{{al-due-end}} days",
    primaryActionLabel: "Start AL self-assessment",
    ctaKind: "assess",
    bucket: "account",
  },
  "rl-low-for-cluster": {
    title: "Relationship Depth Low",
    textTemplate:
      "RL{{rl}} is lower than expected for this account's growth stage. Increasing executive coverage will accelerate alignment progress.",
    whyTemplate: "Account Health in {{growth-clusters}} + RL ≤ {{rl-low-max}}",
    primaryActionLabel: "Review relationships",
    ctaKind: "relationships",
    bucket: "relationship",
  },
  "al-goal-gap": {
    title: "AL Goal Gap",
    textTemplate:
      "AL{{al}} is at least {{al-goal-gap-threshold}} levels below its target of AL{{target_al}} — close the gap before the next milestone review.",
    whyTemplate: "AL ≤ target AL − {{al-goal-gap-threshold}}",
    // Names the action, not "Synopsis →" (CTA label pass, 1 Oct).
    primaryActionLabel: "Advance alignment conversation",
    ctaKind: "synopsis",
    bucket: "account",
  },
  "al-unassessed": {
    title: "AL Unassessed",
    textTemplate:
      "No AL baseline has been established for this account. Without an assessment, alignment-dependent signals cannot fire — schedule an assessment this week.",
    whyTemplate: "No AL assessment on file (or AL0) — data gap",
    primaryActionLabel: "Schedule AL assessment",
    ctaKind: "assess",
    bucket: "account",
  },
  "contacts-missing": {
    title: "No Exec Contacts on Record",
    textTemplate:
      "No exec contacts have been added for this account. Adding contacts is the first step — no mapping or assessment can begin until at least one exec contact is on record.",
    whyTemplate: "contact_stage = none",
    primaryActionLabel: "Add exec contacts",
    ctaKind: "relationships",
    bucket: "account",
  },
  "rl-unmapped": {
    title: "No Contacts Mapped",
    textTemplate:
      "Exec contacts are on record but no pairs have been agreed in the mapping sync. Pair mapping is the prerequisite for RL assessment — schedule the mapping sync this week.",
    whyTemplate: "Contacts added, none mapped to the team yet",
    primaryActionLabel: "Schedule mapping sync",
    ctaKind: "relationships",
    bucket: "relationship",
  },
  "rl-unassessed": {
    title: "Contacts Mapped — RL Not Assessed",
    textTemplate:
      "Contacts are mapped for this account but no RL assessments have been completed. Assessments unlock pair-level scoring and talking point templates.",
    whyTemplate: "contact_stage = mapped, rl = 0",
    primaryActionLabel: "Start RL assessment",
    ctaKind: "relationships",
    bucket: "relationship",
  },
  "sponsor-mismatch": {
    // Named only as a stub signal key in the mock's "Relationship Action
    // Cards" placeholder (Actions tab, act-panel-relationship, ~2248) — the
    // mock never fleshes out copy for it beyond the key itself, so this
    // wording is written from the key's own name, not invented narrative.
    title: "Sponsor Mismatch",
    textTemplate:
      "The mapped executive sponsor doesn't match this account's current sponsor of record — pair mapping may be stale.",
    whyTemplate: "Mapped sponsor ≠ account's sponsor of record",
    primaryActionLabel: "Review relationships",
    ctaKind: "relationships",
    bucket: "relationship",
  },
  balanced: {
    title: "Balanced / On Track",
    textTemplate:
      "Both alignment (AL{{al}}) and relationships (RL{{rl}}) are developing in proportion. Focus on the next AL milestone in the upcoming call.",
    whyTemplate: "Mid AL + Mid RL → Balanced / On Track",
    // Names the action, not "Synopsis →" (CTA label pass, 1 Oct).
    primaryActionLabel: "Plan next AL milestone",
    ctaKind: "synopsis",
    bucket: "account",
  },
};

/** rl-expansion's title/text vary by evidence.expansion_opportunity even
 * though the signal_key is the same — mirrors the mock's two-variant
 * fire site (evaluateSignal() JS ~8862/8865). Falls back to the default
 * "strong" copy in SIGNAL_COPY when the variant field is absent. */
const RL_EXPANSION_POSSIBLE = {
  title: "Possible Expansion Window",
  textTemplate:
    "Growth signals present — prepare a stakeholder mapping session to identify new exec contacts.",
};

export function ctaLabelFor(signalKey: string): string {
  return SIGNAL_COPY[signalKey]?.primaryActionLabel ?? GENERIC_CTA_LABEL;
}

export function ctaKindFor(signalKey: string): CTAKind {
  return SIGNAL_COPY[signalKey]?.ctaKind ?? "synopsis";
}

export function bucketFor(signalKey: string): SignalBucket {
  return SIGNAL_COPY[signalKey]?.bucket ?? "account";
}

/** How a value that's missing for this account reads in card text. A level
 * reads like the card's chips: "AL{{al}}" fills as "AL 2", or "AL ?" with no
 * AL (Sushant, 2026-10-28); other fields read as words. */
const MISSING_TEXT: Record<string, string> = {
  al: "?",
  rl: "?",
  renewal_days: "an unknown number of",
};

/** A firing's triggered fields plus the signal parameter values in force.
 * Parameter ids are hyphenated ({{al-stale-days}}, {{triage-clusters}}) and field names aren't
 * ({{al_stale_days}}), so the two never collide. */
export function templateFields(
  triggeredFields: Record<string, unknown> | undefined | null,
  parameters?: Record<string, number | string> | null
): Record<string, unknown> {
  return { ...(parameters ?? {}), ...(triggeredFields ?? {}) };
}

export function resolveTemplate(
  template: string,
  fields: Record<string, unknown> | undefined | null
): string {
  return template.replace(
    /(AL|RL)?\{\{([\w-]+)\}\}/g,
    (_match, prefix: string | undefined, key: string) => {
      const value = fields?.[key];
      if (value !== undefined && value !== null)
        return prefix ? `${prefix} ${String(value)}` : String(value);
      const missing = MISSING_TEXT[key] ?? "unknown";
      return prefix ? `${prefix} ${missing}` : missing;
    }
  );
}

/** Card text an Izba admin published in Signal Administration → CTA
 * Configuration. Any field left out keeps the default text below. */
export interface CardCopyOverride {
  title?: string;
  text?: string;
  why?: string;
}

/** The default (unfilled) text for a signal, per catalogue variant —
 * what the CTA editor shows before anyone edits it. */
export function defaultCardCopy(
  signalKey: string,
  variant?: string | null
): { title: string; text: string; why: string } {
  const entry = SIGNAL_COPY[signalKey];
  const possible =
    signalKey === "rl-expansion" && variant === "possible"
      ? RL_EXPANSION_POSSIBLE
      : null;
  return {
    title: possible?.title ?? entry?.title ?? signalKey,
    text: possible?.textTemplate ?? entry?.textTemplate ?? "",
    why: entry?.whyTemplate ?? "",
  };
}

/** Mirrors template_errors() in the backend: placeholders must be
 * well-formed {{field}} and carried by this signal, or the card would show
 * raw braces or "?". */
export function templateErrors(text: string, allowed: string[]): string[] {
  const errors: string[] = [];
  const leftover = text.replace(/\{\{[\w-]+\}\}/g, "");
  if (leftover.includes("{") || leftover.includes("}"))
    errors.push("Broken placeholder — write it as {{field}}, e.g. {{al}}.");
  const unknown: string[] = [];
  const pattern = /\{\{([\w-]+)\}\}/g;
  let match = pattern.exec(text);
  while (match) {
    const name = match[1];
    if (allowed.indexOf(name) === -1 && unknown.indexOf(name) === -1)
      unknown.push(name);
    match = pattern.exec(text);
  }
  if (unknown.length)
    errors.push(
      `${unknown.map(n => `{{${n}}}`).join(", ")} ${
        unknown.length === 1 ? "isn't" : "aren't"
      } available for this signal.`
    );
  return errors;
}

export interface ResolvedSignalCopy {
  title: string;
  text: string;
  why: string;
  primaryActionLabel: string;
  ctaKind: CTAKind;
}

/** Single entry point for rendering a signal card's copy — resolves
 * templates against evidence.triggeredFields and picks the rl-expansion
 * variant. Falls back to the raw signal_key as the title (never blank)
 * for any key not yet in the registry, so an unrecognized future rule
 * degrades gracefully instead of rendering empty copy. */
export function resolveSignalCopy(
  signalKey: string,
  firingFields: Record<string, unknown> | undefined | null,
  /** Published card text from Signal Administration, if any. */
  override?: CardCopyOverride | null,
  /** Parameter values in force (from the feed), for {{parameter-id}}. */
  parameters?: Record<string, number | string> | null
): ResolvedSignalCopy {
  const triggeredFields = templateFields(firingFields, parameters);
  const entry = SIGNAL_COPY[signalKey];
  if (!entry) {
    // No built-in copy (e.g. a proposed signal): use whatever card text
    // was given rather than showing the raw key.
    return {
      title: resolveTemplate(override?.title || signalKey, triggeredFields),
      text: resolveTemplate(override?.text || "", triggeredFields),
      why: resolveTemplate(override?.why || "", triggeredFields),
      primaryActionLabel: GENERIC_CTA_LABEL,
      ctaKind: "synopsis",
    };
  }
  const variant =
    signalKey === "rl-expansion" &&
    triggeredFields?.expansion_opportunity === "possible"
      ? RL_EXPANSION_POSSIBLE
      : null;
  return {
    title: resolveTemplate(
      override?.title || variant?.title || entry.title,
      triggeredFields
    ),
    text: resolveTemplate(
      override?.text || variant?.textTemplate || entry.textTemplate,
      triggeredFields
    ),
    why: resolveTemplate(override?.why || entry.whyTemplate, triggeredFields),
    primaryActionLabel: entry.primaryActionLabel,
    ctaKind: entry.ctaKind,
  };
}

/** @deprecated kept for any lingering import — prefer resolveSignalCopy().
 * Superseded by SIGNAL_COPY[key].title, consolidated from ActionCard.tsx's
 * former local SIGNAL_TITLES map. */
export const SIGNAL_CTA_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(SIGNAL_COPY).map(([key, entry]) => [
    key,
    entry.primaryActionLabel,
  ])
);

/** A card's CTA label + route: the backend's effective CTA (which carries
 * any Izba-admin override) when present, else this file's defaults. */
export function cardCta(card: {
  signal_key: string;
  cta?: { label: string; kind: CTAKind; path?: string | null } | null;
}): { label: string; kind: CTAKind; path?: string } {
  if (card.cta?.label && card.cta.kind) {
    // A "path" CTA without a path can't route anywhere — send it to the
    // Synopsis like every other unrouted card.
    if (card.cta.kind === "path" && !card.cta.path) {
      return { label: card.cta.label, kind: "synopsis" };
    }
    return {
      label: card.cta.label,
      kind: card.cta.kind,
      path: card.cta.path ?? undefined,
    };
  }
  return {
    label: ctaLabelFor(card.signal_key),
    kind: ctaKindFor(card.signal_key),
  };
}

/** A Target Registry path with its {account_id} placeholder filled in. */
export function fillCtaPath(path: string, accountId: string): string {
  return path.split("{account_id}").join(encodeURIComponent(accountId));
}

/** What was live when a CTA was clicked (stamped server-side since
 * 2026-09-30), as one short line for Action History. */
export function clickContextText(event: {
  guide?: string | null;
  card_text?: string | null;
  config_version?: number | null;
  guide_step?: string | null;
  done?: boolean | null;
}): string {
  if (event.guide_step)
    return ` · “${event.guide_step}” ${event.done ? "ticked" : "unticked"}`;
  const parts: string[] = [];
  if (event.guide) parts.push(`via guide “${event.guide}”`);
  if (event.card_text === "edited") parts.push("edited card text");
  if (event.config_version) parts.push(`config v${event.config_version}`);
  return parts.length ? ` · ${parts.join(" · ")}` : "";
}

/** A firing's resolution reason in words. "covered_by:<key>" (a Rule
 * Classifier rule) names the signal that now stands in for it instead of
 * showing the raw key. */
export function resolutionReasonLabel(reason: string | null): string {
  if (!reason) return "";
  if (reason.startsWith("covered_by:")) {
    const key = reason.slice("covered_by:".length);
    return `covered by ${SIGNAL_COPY[key]?.title ?? key}`;
  }
  if (reason === "condition_cleared:resolved_after_action")
    return "resolved after action";
  return reason.replace(/_/g, " ");
}
